import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { usePageTitle } from "../lib/usePageTitle";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Inbox, Send, FileText, Trash2, Star, PenSquare, Search, ArrowLeft,
  Mail as MailIcon, RefreshCw, Loader2, X, Paperclip, Reply, Download,
} from "lucide-react";

const FOLDERS = [
  { id: "inbox", label: "Boîte de réception", icon: Inbox },
  { id: "starred", label: "Suivis", icon: Star },
  { id: "sent", label: "Envoyés", icon: Send },
  { id: "drafts", label: "Brouillons", icon: FileText },
  { id: "trash", label: "Corbeille", icon: Trash2 },
];

function formatDate(d) {
  try {
    const dt = new Date(d);
    const now = new Date();
    if (dt.toDateString() === now.toDateString()) {
      return dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return dt.toLocaleDateString();
  } catch { return ""; }
}

function avatarColor(s) {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `hsl(${hue} 65% 50%)`;
}

function formatSize(n) {
  if (!n) return "0 o";
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / 1024 / 1024).toFixed(2)} Mo`;
}

export default function OmniMailPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  usePageTitle("OmniMail");
  const [folder, setFolder] = useState("inbox");
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [filter, setFilter] = useState("");
  const [composing, setComposing] = useState(false);
  const [replyTo, setReplyTo] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) navigate("/login", { state: { from: "/mail" } });
  }, [user, loading, navigate]);

  const load = useCallback(async (f = folder) => {
    setLoadingList(true);
    try {
      const { data } = await api.get(`/mail`, { params: { folder: f } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } finally { setLoadingList(false); }
  }, [folder]);

  useEffect(() => { if (user && user !== false) load(folder); }, [folder, user, load]);

  const openMail = async (id) => {
    try {
      const { data } = await api.get(`/mail/${id}`);
      setSelected(data);
      setItems((prev) => prev.map((m) => m.id === id ? { ...m, read: true } : m));
      if (folder === "inbox") setUnread((n) => Math.max(0, n - (items.find((m) => m.id === id)?.read ? 0 : 1)));
    } catch {}
  };

  const toggleStar = async (id) => {
    try {
      const { data } = await api.post(`/mail/${id}/star`);
      setItems((prev) => prev.map((m) => m.id === id ? { ...m, starred: data.starred } : m));
      if (selected?.id === id) setSelected({ ...selected, starred: data.starred });
    } catch {}
  };

  const trash = async (id) => {
    try {
      await api.delete(`/mail/${id}`);
      setItems((prev) => prev.filter((m) => m.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch {}
  };

  const downloadAttachment = async (mailId, idx, fileName) => {
    try {
      const { data } = await api.get(`/mail/${mailId}/attachment/${idx}`);
      const bin = atob(data.content_b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: data.mime });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = data.name || fileName;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  if (loading || !user || user === false) return null;

  const filtered = items.filter((m) =>
    !filter ||
    m.subject?.toLowerCase().includes(filter.toLowerCase()) ||
    m.from_email?.toLowerCase().includes(filter.toLowerCase()) ||
    m.body?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="mail-page">
      <Header compact />
      <div className="border-b border-[var(--omni-border)] px-4 sm:px-6 py-3 flex items-center gap-3">
        <h1 className="text-2xl font-black tracking-tight flex items-center gap-2">
          <MailIcon size={22} className="text-[var(--omni-accent)]" /> OmniMail
        </h1>
        <div className="flex-1 max-w-xl relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={15} />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Rechercher dans le courrier" className="pl-9 rounded-full bg-[var(--omni-surface)] border-0" data-testid="mail-search-input" />
        </div>
        <Button variant="ghost" onClick={() => load()} title="Actualiser" data-testid="mail-refresh-btn"><RefreshCw size={15} /></Button>
      </div>

      <div className="flex-1 flex min-h-0">
        <aside className="w-60 border-r border-[var(--omni-border)] p-3 hidden md:flex flex-col">
          <Button
            onClick={() => { setReplyTo(null); setComposing(true); }}
            data-testid="mail-compose-btn"
            className="rounded-full bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white justify-start gap-3 shadow-sm h-12 mb-3"
          >
            <PenSquare size={16} /> Nouveau message
          </Button>
          <nav className="space-y-0.5">
            {FOLDERS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => { setFolder(id); setSelected(null); }}
                data-testid={`mail-folder-${id}`}
                className={`w-full flex items-center justify-between gap-3 px-4 py-2 rounded-full text-sm transition ${folder === id ? "bg-[var(--omni-surface)] font-semibold" : "hover:bg-[var(--omni-surface)]"}`}
              >
                <span className="flex items-center gap-3"><Icon size={15} /> {label}</span>
                {id === "inbox" && unread > 0 && (
                  <span className="text-xs font-semibold text-[var(--omni-accent)]">{unread}</span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        <main className="flex-1 min-w-0 flex">
          {!selected ? (
            <div className="flex-1 min-w-0">
              {loadingList && (
                <div className="p-8 text-gray-500 inline-flex items-center"><Loader2 className="animate-spin mr-2" size={16} />Chargement…</div>
              )}
              {!loadingList && filtered.length === 0 && (
                <div className="p-16 text-center text-gray-500">
                  Aucun message dans <strong>{FOLDERS.find((f) => f.id === folder)?.label}</strong>.
                </div>
              )}
              <ul className="divide-y divide-[var(--omni-border)]">
                {filtered.map((m) => (
                  <li
                    key={m.id}
                    data-testid={`mail-row-${m.id}`}
                    onClick={() => openMail(m.id)}
                    className={`flex items-center gap-3 px-4 sm:px-6 py-3 hover:bg-[var(--omni-surface)] cursor-pointer ${!m.read && folder === "inbox" ? "font-semibold bg-orange-50/40" : ""}`}
                  >
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleStar(m.id); }}
                      data-testid={`mail-star-${m.id}`}
                      className={`p-1 ${m.starred ? "text-[var(--omni-accent)]" : "text-gray-300 hover:text-gray-500"}`}
                    >
                      <Star size={16} fill={m.starred ? "currentColor" : "none"} />
                    </button>
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                      style={{ background: avatarColor(folder === "sent" ? m.to_email : m.from_email) }}
                    >
                      {((folder === "sent" ? m.to_email : (m.from_name || m.from_email)) || "?")[0].toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1 grid grid-cols-[140px_1fr] gap-3 items-center">
                      <div className="truncate text-sm">{folder === "sent" ? m.to_email : (m.from_name || m.from_email)}</div>
                      <div className="truncate text-sm flex items-center gap-2">
                        <span>{m.subject || "(sans objet)"}</span>
                        {m.attachments && m.attachments.length > 0 && <Paperclip size={12} className="text-gray-400 flex-shrink-0" />}
                        <span className="text-gray-500 font-normal">— {m.body?.slice(0, 100)}</span>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 flex-shrink-0">{formatDate(m.created_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="flex-1 min-w-0 overflow-y-auto p-6 sm:p-10 max-w-4xl mx-auto" data-testid="mail-detail">
              <button onClick={() => setSelected(null)} className="text-sm text-gray-500 hover:text-black mb-4 inline-flex items-center gap-1" data-testid="mail-back-btn">
                <ArrowLeft size={14} /> Retour
              </button>
              <div className="flex items-start justify-between mb-2">
                <h2 className="text-3xl font-black tracking-tight">{selected.subject || "(sans objet)"}</h2>
                <div className="flex items-center gap-1">
                  <button onClick={() => { setReplyTo(selected); setComposing(true); }} className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-black" data-testid="mail-detail-reply" title="Répondre">
                    <Reply size={18} />
                  </button>
                  <button onClick={() => toggleStar(selected.id)} className={`p-2 rounded-md hover:bg-gray-100 ${selected.starred ? "text-[var(--omni-accent)]" : "text-gray-400"}`} data-testid="mail-detail-star">
                    <Star size={18} fill={selected.starred ? "currentColor" : "none"} />
                  </button>
                  <button onClick={() => trash(selected.id)} className="p-2 rounded-md hover:bg-gray-100 text-gray-400 hover:text-red-600" data-testid="mail-detail-trash">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-3 py-3 border-y border-[var(--omni-border)] mb-6">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                  style={{ background: avatarColor(selected.from_email) }}
                >
                  {(selected.from_name || selected.from_email || "?")[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {selected.from_name ? `${selected.from_name} ` : ""}<span className="text-gray-500 font-normal">&lt;{selected.from_email}&gt;</span>
                  </div>
                  <div className="text-xs text-gray-500">à {selected.to_email}</div>
                </div>
                <div className="text-xs text-gray-500">{new Date(selected.created_at).toLocaleString()}</div>
              </div>
              <div className="whitespace-pre-wrap text-base leading-relaxed mb-6">{selected.body}</div>

              {selected.attachments && selected.attachments.length > 0 && (
                <div className="border-t border-[var(--omni-border)] pt-4" data-testid="mail-attachments">
                  <div className="text-xs font-bold uppercase text-gray-500 mb-3">Pièces jointes ({selected.attachments.length})</div>
                  <div className="flex flex-wrap gap-2">
                    {selected.attachments.map((a, i) => (
                      <button
                        key={i}
                        onClick={() => downloadAttachment(selected.id, i, a.name)}
                        data-testid={`mail-attachment-${i}`}
                        className="border border-[var(--omni-border)] rounded-lg px-3 py-2 hover:bg-[var(--omni-surface)] inline-flex items-center gap-2 text-sm"
                      >
                        <Paperclip size={13} className="text-gray-400" />
                        <span className="truncate max-w-[180px]">{a.name}</span>
                        <span className="text-xs text-gray-400">{formatSize(a.size)}</span>
                        <Download size={12} className="text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {composing && (
        <ComposeWindow
          fromEmail={user.email}
          replyTo={replyTo}
          onClose={() => { setComposing(false); setReplyTo(null); }}
          onSent={() => { setComposing(false); setReplyTo(null); toast.success("Message envoyé"); load(folder === "sent" ? "sent" : folder); }}
        />
      )}
    </div>
  );
}

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function ComposeWindow({ fromEmail, replyTo, onClose, onSent }) {
  const isReply = !!replyTo;
  const replyTarget = replyTo ? (replyTo.from_email === fromEmail ? replyTo.to_email : replyTo.from_email) : "";
  const replyQuote = replyTo ? `\n\n----- Le ${new Date(replyTo.created_at).toLocaleString()}, ${replyTo.from_email} a écrit :\n${(replyTo.body || "").split("\n").map((l) => "> " + l).join("\n")}` : "";
  const [to, setTo] = useState(replyTarget);
  const [subject, setSubject] = useState(replyTo ? ("Re: " + (replyTo.subject || "").replace(/^Re:\s*/i, "")) : "");
  const [body, setBody] = useState(replyQuote);
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInput = useRef(null);

  const onPickFile = async (e) => {
    const list = Array.from(e.target.files || []);
    const accepted = [];
    for (const f of list) {
      if (f.size > 1024 * 1024) { toast.error(`${f.name} : 1 Mo max`); continue; }
      const b64 = await fileToBase64(f);
      accepted.push({ name: f.name, mime: f.type || "application/octet-stream", size: f.size, content_b64: b64 });
    }
    setAttachments((a) => [...a, ...accepted]);
    e.target.value = "";
  };

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      if (isReply) {
        await api.post(`/mail/${replyTo.id}/reply`, { body, subject, attachments });
      } else {
        if (attachments.length > 0) {
          await api.post(`/mail/send-rich?to_email=${encodeURIComponent(to.trim().toLowerCase())}&subject=${encodeURIComponent(subject)}`, { body, attachments });
        } else {
          await api.post("/mail/send", { to_email: to.trim().toLowerCase(), subject, body });
        }
      }
      onSent();
      try { window.dispatchEvent(new Event("omni:ad:mail_sent")); } catch {}
    } catch (err) {
      toast.error("Échec de l'envoi");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed bottom-4 right-4 w-[min(620px,calc(100vw-2rem))] z-50 bg-white border border-[var(--omni-border)] rounded-lg shadow-2xl flex flex-col" data-testid="mail-compose">
      <div className="flex items-center justify-between px-3 py-2 bg-[var(--omni-surface)] rounded-t-lg">
        <div className="text-sm font-medium">{isReply ? "Répondre" : "Nouveau message"}</div>
        <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded" data-testid="mail-compose-close"><X size={14} /></button>
      </div>
      <form onSubmit={send} className="p-3 space-y-2">
        <div className="text-xs text-gray-500">De · <span className="text-black">{fromEmail}</span></div>
        <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="À" required type="email" disabled={isReply} className="border-0 border-b border-[var(--omni-border)] rounded-none px-0 focus-visible:ring-0" data-testid="mail-compose-to" />
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Objet" className="border-0 border-b border-[var(--omni-border)] rounded-none px-0 focus-visible:ring-0" data-testid="mail-compose-subject" />
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Votre message…" rows={10} className="border-0 px-0 resize-none focus-visible:ring-0" data-testid="mail-compose-body" />

        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="mail-compose-attachments">
            {attachments.map((a, i) => (
              <span key={i} className="inline-flex items-center gap-1 bg-[var(--omni-surface)] rounded-full px-2 py-1 text-xs">
                <Paperclip size={10} /> {a.name} · {formatSize(a.size)}
                <button type="button" onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500 ml-1">
                  <X size={10} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-[var(--omni-border)]">
          <div className="flex items-center gap-2">
            <Button type="submit" disabled={sending || (!isReply && !to.trim())} className="bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white rounded-full px-5" data-testid="mail-compose-send">
              {sending ? "Envoi…" : "Envoyer"}
            </Button>
            <input ref={fileInput} type="file" multiple onChange={onPickFile} className="hidden" />
            <button type="button" onClick={() => fileInput.current?.click()} className="p-2 rounded-full hover:bg-[var(--omni-surface)]" data-testid="mail-compose-attach" title="Joindre un fichier">
              <Paperclip size={15} />
            </button>
          </div>
          <button type="button" onClick={onClose} className="text-sm text-gray-500 hover:text-black">Annuler</button>
        </div>
      </form>
    </div>
  );
}
